import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Radio } from 'lucide-react';
import { useRadio } from '../context/RadioContext';
import { DesktopNav } from './nav/DesktopNav';
import { ListenLiveButton } from './nav/ListenLiveButton';
import { MobileMenu } from './nav/MobileMenu';
import { NAV_STYLES } from './nav/NavStyles';

const NAV_LINKS = [
  { name: 'Home', path: '/' },
  { name: 'Releases', path: '/releases' },
  { name: 'Artists', path: '/artists' },
  { name: 'Gallery', path: '/gallery' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
];

export const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const navRef = useRef(null);
  const location = useLocation();
  
  const { radioSettings, isRadioActive, toggleRadio, openRadio } = useRadio();

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (navRef.current) {
        const rect = navRef.current.getBoundingClientRect();
        setMousePosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      }
    };
    const nav = navRef.current;
    if (nav) {
      nav.addEventListener('mousemove', handleMouseMove);
      return () => nav.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <>
      <nav 
        ref={navRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ease-out ${
          scrolled ? 'py-2' : 'py-4'
        }`}
      >
        {/* Scrolled glassmorphism background */}
        <div 
          className={`absolute inset-x-4 md:inset-x-8 top-2 bottom-2 rounded-2xl transition-all duration-700 ${
            scrolled ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            background: 'linear-gradient(135deg, rgba(249,115,22,0.1) 0%, rgba(34,197,94,0.1) 100%)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)'
          }}
        />
        
        {/* Mouse follow glow */}
        <div 
          className="pointer-events-none absolute rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            width: '200px', height: '200px',
            left: mousePosition.x - 100, top: mousePosition.y - 100,
            background: 'radial-gradient(circle, rgba(249,115,22,0.15) 0%, transparent 70%)',
            filter: 'blur(20px)',
          }}
        />

        <div className="container mx-auto px-4 lg:px-8 relative">
          <div className="flex items-center justify-between h-14 md:h-16">
            
            {/* Left - DharmaR wordmark */}
            <div className="flex items-center shrink-0">
              <Link to="/" data-testid="sun-logo-link" className="group">
                <span
                  className="text-2xl md:text-3xl font-extrabold uppercase tracking-[0.22em] bg-gradient-to-r from-orange-500 via-amber-400 to-lime-400 bg-clip-text text-transparent transition-all duration-300 group-hover:tracking-[0.3em] group-hover:drop-shadow-[0_0_16px_rgba(191,255,0,0.6)]"
                  style={{ fontFamily: "'Orbitron', sans-serif" }}
                >
                  DHARMAR
                </span>
              </Link>
            </div>

            {/* Center - Listen Live (Desktop) */}
            <div className="hidden lg:flex items-center justify-center">
              <ListenLiveButton 
                radioSettings={radioSettings} 
                isRadioActive={isRadioActive} 
                toggleRadio={toggleRadio} 
              />
            </div>

            {/* Right - Desktop Nav Links */}
            <DesktopNav 
              navLinks={NAV_LINKS} 
              isActive={isActive} 
              hoveredIndex={hoveredIndex} 
              setHoveredIndex={setHoveredIndex} 
            />

            {/* Mobile: Radio + Hamburger */}
            <div className="flex items-center gap-2 lg:hidden">
              {radioSettings?.is_active && (
                <button
                  onClick={toggleRadio}
                  className="group relative overflow-hidden"
                  data-testid="mobile-radio-toggle-btn"
                >
                  <div className={`relative flex items-center gap-1.5 px-3 py-2 rounded-full transition-all duration-300 ${
                    isRadioActive ? 'text-white' : 'text-orange-400 bg-black/40 backdrop-blur-sm'
                  }`}>
                    <Radio className={`w-4 h-4 transition-transform duration-300 ${
                      isRadioActive ? 'animate-pulse scale-110' : 'group-hover:scale-110'
                    }`} />
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                    </span>
                  </div>
                </button>
              )}
              <button
                className="relative z-[70] w-12 h-12 flex items-center justify-center group"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Toggle menu"
                data-testid="mobile-menu-btn"
              >
                <div className={`absolute inset-0 rounded-full border-2 transition-all duration-500 ${
                  isMobileMenuOpen 
                    ? 'border-orange-500 scale-100 rotate-180' 
                    : 'border-orange-500/30 scale-90 rotate-0 group-hover:scale-100 group-hover:border-orange-500/60'
                }`} />
                <div className={`absolute inset-0 rounded-full bg-orange-500/20 blur-lg transition-opacity duration-500 ${
                  isMobileMenuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'
                }`} />
                <div className="relative w-6 h-5 flex flex-col justify-center items-center">
                  <span className={`absolute h-0.5 w-6 bg-orange-400 rounded-full transition-all duration-300 ${
                    isMobileMenuOpen ? 'rotate-45 translate-y-0' : '-translate-y-2'
                  }`} />
                  <span className={`absolute h-0.5 w-6 bg-orange-400 rounded-full transition-all duration-300 ${
                    isMobileMenuOpen ? 'opacity-0 scale-0' : 'opacity-100 scale-100'
                  }`} />
                  <span className={`absolute h-0.5 w-6 bg-orange-400 rounded-full transition-all duration-300 ${
                    isMobileMenuOpen ? '-rotate-45 translate-y-0' : 'translate-y-2'
                  }`} />
                </div>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <MobileMenu
          navLinks={NAV_LINKS}
          isActive={isActive}
          radioSettings={radioSettings}
          onClose={() => setIsMobileMenuOpen(false)}
          openRadio={openRadio}
        />
      )}

      <style>{NAV_STYLES}</style>
    </>
  );
};
